/**
 * In-game HUD (plain DOM). Layout shell (layout.ts) with screen regions; builders fill them with
 * widgets registered in widgets.ts. Until a widget replaces them, the legacy pieces (unit frames,
 * action bar, keystone panel, meters) are built into their regions here. Nameplates, floating
 * text, errors and the death box live directly on the HUD root (screen space, unscaled).
 */
import type { ContentRegistry, DeepReadonly, SimEvent, SimQueries, UnitId, World } from '@keystone/sim';
import type { MeterMode, MeterRow, MeterScope } from '../game/meters';
import type { Renderer, RenderFrame, RenderUnit } from '../render/types';
import type { UiBind } from '../input/bindings';
import { ActionBar } from './actionBar';
import { el, setClass, setText } from './dom';
import { FloatingText } from './floatingText';
import { KeystonePanel } from './keystonePanel';
import { HudLayout, type HudRegions } from './layout';
import { MeterPanel } from './meterPanel';
import { Nameplates } from './nameplates';
import { type CastBarView, UnitFrame, createCastBar } from './unitFrame';
import { PartyFrames } from './frames/partyFrames';
import { TargetFrame } from './frames/targetFrame';
import { AutoAttackHud } from './autoAttack'; // +autoattack
import { HUD_WIDGETS, type HudWidget, type HudWidgetDef, replacedPieces } from './widgets';
import { perf } from '../debug/perf';
import { InRunHud } from '../meta/inrunHud';
import type { InRunTally, TallyTick } from '../meta/inrunTally';
import { lootToastWidget } from './loot/lootToasts'; // +world-resources
import { UiErrorLine, type UiErrorTone } from './uiErrors'; // +castux
import { castCues, interruptAbilityOf, kickReadyNow, noteInterrupts, resetCastCues } from './castCues'; // +castux
import { RangeChecker } from './rangeCheck'; // +castux
import { ResourceWarnings } from './resourceWarnings'; // +castux
import { ChatFrame, SpeechBubbles } from './chat/chatFrame'; // +personas
import { rewardPops } from './field/rewardPops'; // +field: one reward-pop system for fields and dungeons
import { type ChatLine, socialLines } from './chat/model'; // +personas, +presence (ChatLine)
import type { Presence } from '@keystone/sim';

export interface HudHandlers {
  bind(bind: UiBind): void;
  /** Optional: the Dodge (Space) button. */
  dodge?(): void;
  select(id: UnitId): void;
  /** Optional: dead player presses "Release". */
  release?(): void;
  /** +personas: the player sent a party chat line (chat input box). */
  say?(text: string): void;
}

export interface HudUpdate {
  world: DeepReadonly<World>;
  content: ContentRegistry;
  queries: SimQueries;
  playerId: UnitId | null;
  selectedId: UnitId | null;
  hoverId: UnitId | null;
  frame: RenderFrame;
  unitsById: ReadonlyMap<UnitId, RenderUnit>;
  renderer: Renderer;
  focus: { x: number; y: number };
  meterRows(mode: MeterMode, scope?: MeterScope): MeterRow[];
  status: string;
}

interface MountedWidget {
  def: HudWidgetDef;
  widget: HudWidget;
  label: string;
}

export class Hud {
  readonly root: HTMLElement;
  /** Layout shell: `layout.regions.<name>` are the mount points. */
  readonly layout: HudLayout;
  readonly regions: HudRegions;
  /** Party unit under the mouse on a unit frame (mouse-over healing). Widgets replacing the party frames set it too. */
  frameHoverId: UnitId | null = null;
  private readonly handlers: HudHandlers;
  private readonly nameplates: Nameplates;
  private readonly floaters: FloatingText;
  private readonly widgets: MountedWidget[] = [];
  // Legacy pieces (null once a widget replaces them).
  private readonly party: PartyFrames | null = null;
  private readonly playerFrame: UnitFrame | null = null;
  private readonly targetFrame: TargetFrame | null = null;
  private readonly autoAttack: AutoAttackHud; // +autoattack
  private readonly playerCast: CastBarView | null = null;
  private readonly actionBar: ActionBar | null = null;
  private readonly keystone: KeystonePanel | null = null;
  private readonly meters: MeterPanel | null = null;
  /** In-run progression: XP bar, purse, level-up banner, kill-reward pops. */
  private readonly progress: InRunHud;
  /** +castux: the WoW-style UI error line (dedupe, rate-limited sound). */
  private readonly error: UiErrorLine;
  private readonly status: HTMLElement;
  private readonly deathBox: HTMLElement;
  /** +castux: range / LoS dimming, resource warnings, the player's interrupt (kick colour on enemy bars). */
  private readonly range = new RangeChecker();
  private readonly dimFor = (id: UnitId): boolean => this.range.dim(id);
  private readonly resWarn = new ResourceWarnings();
  private kickClass: string | null | undefined = undefined;
  private kickId: string | null = null;
  private lastWorld: DeepReadonly<World> | null = null;
  private targetDim = false;
  private lastMeterAt = -Infinity;
  /** +personas: party chat frame, speech bubbles, each member's last away state. */
  private readonly chat: ChatFrame;
  private readonly bubbles: SpeechBubbles;
  private readonly away = new Map<UnitId, Presence>();
  private content: ContentRegistry | null = null;

  constructor(parent: HTMLElement, handlers: HudHandlers, widgets: readonly HudWidgetDef[] = HUD_WIDGETS) {
    this.handlers = handlers;
    this.root = el('div', 'hud-root', parent);
    this.nameplates = new Nameplates(this.root, (id) => handlers.select(id));
    this.floaters = new FloatingText(this.root);
    this.layout = new HudLayout(this.root);
    const R = (this.regions = this.layout.regions);
    const off = replacedPieces(widgets);

    if (!off.has('partyFrames')) {
      this.party = new PartyFrames(R.party, { select: (id) => handlers.select(id), hover: (id) => (this.frameHoverId = id) });
      this.party.inSight = (id) => this.range.inSight(id); // +castux: out-of-sight members dim like out-of-range ones
    }
    if (!off.has('targetFrame')) this.targetFrame = new TargetFrame(R.target, (id) => handlers.select(id));
    if (!off.has('playerCast')) this.playerCast = createCastBar(R.bottomCenter, 'player-cast');
    if (!off.has('playerFrame')) this.playerFrame = new UnitFrame(el('div', 'main-frames', R.bottomCenter), { size: 'large', showAuras: true });
    // +autoattack: swing timer (above the player frame) and the target frame's crossed swords.
    this.autoAttack = new AutoAttackHud(R.bottomCenter, R.bottomCenter.querySelector<HTMLElement>('.main-frames'), this.targetFrame?.root ?? null);
    // The legacy dock hosts the action bar and the in-run XP bar (which spans its width). When the
    // skill cluster replaces the action bar it draws its own XP hairline (from meta inRunView).
    const dock = off.has('actionBar') ? null : el('div', 'ir-dock', R.bottomCenter);
    if (dock) this.actionBar = new ActionBar(dock, handlers.bind);
    if (!off.has('keystone')) this.keystone = new KeystonePanel(R.right);
    if (!off.has('meters')) this.meters = new MeterPanel(R.right);
    this.progress = new InRunHud({ dock, keystone: this.keystone?.root ?? R.right, root: this.root, pop: (p, now) => (rewardPops.current ? rewardPops.current.add(p.pos, [{ kind: 'xp', amount: p.xp }, { kind: 'gold', amount: p.gold }]) : this.floaters.reward(p, now)) });

    for (const def of widgets) this.addWidget(def);
    this.addWidget(lootToastWidget); // +world-resources: pickup toasts (hud/loot)

    this.bubbles = new SpeechBubbles(this.root); // +personas
    this.chat = new ChatFrame(this.root, handlers.say ? (text) => handlers.say?.(text) : undefined); // +personas
    this.error = new UiErrorLine(this.root);
    this.status = el('div', 'status-text', this.root);
    this.deathBox = el('div', 'death-box', this.root);
    el('div', 'death-title', this.deathBox, 'You have died');
    el('div', '', this.deathBox, 'You release automatically once the party leaves combat, or release now to run back from the checkpoint.');
    if (handlers.release) {
      const btn = el('button', 'death-release', this.deathBox, 'Release');
      btn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        handlers.release?.();
      });
    }
  }

  /** Mount a widget into its region (normally via HUD_WIDGETS; `replaces` only applies there). */
  addWidget(def: HudWidgetDef): HudWidget {
    const host = el('div', `hk-w hk-w-${def.id}`, this.regions[def.region]);
    host.dataset['widget'] = def.id;
    const widget = def.create(host, { layout: this.layout, regions: this.regions, handlers: this.handlers, root: this.root });
    this.widgets.push({ def, widget, label: `hud.${def.id}` });
    return widget;
  }

  /** +castux: one short red line under the target frame (repeats extend it; soft, rate-limited error sound). */
  showError(text: string, now: number, tone: UiErrorTone = 'error'): void {
    this.error.show(text, now, tone);
  }

  /** +castux: a gentle amber warning on the same line (no sound). */
  warn(text: string, now: number): void {
    this.error.show(text, now, 'warn');
  }

  /** Graphics "effects intensity": scales the combat-text pop punch alongside the spell effects. */
  setFxIntensity(k: number): void {
    this.floaters.model.fxIntensity = k;
  }

  /** Start the in-run progression widgets from the run's tally (null hides them). */
  beginProgress(tally: InRunTally | null): void {
    this.progress.begin(tally?.start ?? null, tally?.hero ?? null);
  }

  /** A sim tick paid kill rewards (pops wait for the killing blow's damage text). */
  rewards(tick: TallyTick, now: number): void {
    this.progress.apply(tick, now);
  }

  /**
   * +personas: party social events ('chat', 'presence', 'party_leave') of the ticks since the last
   * frame, in order: chat frame, speech bubbles, AFK tags, "has left the group".
   */
  social(events: readonly SimEvent[], world: DeepReadonly<World>, now: number): void {
    if (!this.content) return;
    const lines = socialLines(events, world, this.content, this.away);
    this.chat.add(lines, now);
    for (const l of lines) if (l.unitId && l.kind !== 'system') this.bubbles.show(l.unitId, l.text, l.kind === 'emote', now);
  }

  /** +presence: zone chat lines (net/fieldPresence.ts) into the chat frame and speech bubbles; sets the input's channel. */
  zoneChat(lines: readonly ChatLine[], now: number, channel: string | null): void {
    this.chat.setChannel(channel);
    if (!lines.length) return;
    this.chat.add(lines, now);
    for (const l of lines) if (l.unitId && l.kind !== 'system') this.bubbles.show(l.unitId, l.text, l.kind === 'emote', now);
  }

  ingest(events: readonly SimEvent[], playerId: UnitId | null, now: number): void {
    if (this.lastWorld) noteInterrupts(events, this.lastWorld, playerId, now); // +castux: "Interrupted by <name>"
    this.floaters.ingest(events, playerId, now);
    this.progress.ingestText(events);
    for (const w of this.widgets) w.widget.ingest?.(events, playerId, now);
  }

  update(h: HudUpdate, realNow: number): void {
    const { world, content } = h;
    this.content = content; // +personas: social() names speakers with it
    this.castux(h, realNow);
    perf.begin('hud.frames');
    this.party?.update(world, content, h.queries, h.playerId, h.selectedId, h.hoverId, realNow);
    if (this.playerFrame) {
      this.playerFrame.unitId = h.playerId;
      this.playerFrame.update(world, content);
    }
    // +personas: a member who left the group is no longer a target.
    const targetId = h.selectedId && world.units[h.selectedId]?.departed ? null : h.selectedId;
    this.targetFrame?.update(world, content, targetId, h.playerId, realNow);
    this.autoAttack.update(world, h.playerId, targetId, h.frame.timeMs); // +autoattack
    // +castux: the target frame greys out when the target is out of range of your main ability or out of sight.
    const tdim = this.range.dim(h.selectedId);
    if (tdim !== this.targetDim && this.targetFrame) {
      this.targetDim = tdim;
      setClass(this.targetFrame.root, 'oor', tdim);
    }
    this.playerCast?.update(h.playerId ? world.units[h.playerId] : undefined, world, content);
    perf.end('hud.frames');
    perf.begin('hud.actionbar');
    if (this.actionBar) {
      setClass(this.actionBar.root, 'hidden', !h.playerId);
      if (h.playerId) this.actionBar.update(world, content, h.queries, h.playerId, h.selectedId, h.hoverId);
    }
    perf.end('hud.actionbar');
    perf.begin('hud.meters');
    this.keystone?.update(world, content);
    if (this.meters && realNow - this.lastMeterAt > 250) {
      this.lastMeterAt = realNow;
      this.meters.update(h.meterRows(this.meters.mode, this.meters.scope), this.meters.scopeLabel(h.world));
    }
    perf.end('hud.meters');
    for (const w of this.widgets) {
      perf.begin(w.label);
      w.widget.update(h, realNow);
      perf.end(w.label);
    }
    perf.begin('hud.plates');
    this.nameplates.update(world, content, h.frame.units, h.renderer, h.focus, h.selectedId, h.playerId, this.dimFor);
    perf.end('hud.plates');
    this.bubbles.update(realNow, h.unitsById, h.renderer); // +personas
    this.chat.update(realNow, !!h.playerId); // +personas: spectators can't type
    perf.begin('hud.floaters');
    this.progress.update(realNow);
    this.floaters.update(realNow, h.unitsById, h.renderer, h.selectedId);
    perf.end('hud.floaters');
    this.error.update(realNow);
    setText(this.status, h.status);
    const me = h.playerId ? world.units[h.playerId] : undefined;
    setClass(this.deathBox, 'show', !!me && !me.alive);
  }

  /** +castux: per-frame shared state: range checks, the kick-ready cue, resource warnings, proc names. */
  private castux(h: HudUpdate, realNow: number): void {
    const { world, content } = h;
    this.lastWorld = world;
    this.floaters.setContent(content);
    this.range.begin(world, h.queries, content, h.playerId, realNow);
    const me = h.playerId ? world.units[h.playerId] : undefined;
    const classId = me?.classId ?? null;
    if (classId !== this.kickClass) {
      this.kickClass = classId;
      this.kickId = interruptAbilityOf(classId ? content.class(classId) : undefined, (id) => content.ability(id));
    }
    castCues.kickReady = kickReadyNow(world, h.queries, content, h.playerId, this.kickId);
    const warning = this.resWarn.update(me, classId ? content.class(classId) : undefined, world.time);
    if (warning) this.warn(warning, realNow);
  }

  dispose(): void {
    resetCastCues(); // +castux
    for (const w of this.widgets) w.widget.dispose?.();
    this.widgets.length = 0;
    this.floaters.clear();
    this.progress.dispose();
    this.layout.dispose();
    this.chat.dispose(); // +personas
    this.bubbles.dispose();
    this.root.remove();
  }
}
